import { setup } from 'axios-cache-adapter';
import getEnv from '@/utils/env';
import queryErrorHandler from '@/utils/errorHandler';
import store from '../store';

const qs = require('querystring-browser');

class CmsUtils {
  // Create `axios` instance with pre-configured `axios-cache-adapter` attached to it
  constructor() {
    this.instance = null;
    this.instanceWithoutHeader = null;
    this.instanceUpload = null;
  }

  // ----------------------------------- AUTH
  async preQuery() {
    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    if (store.getters.idToken && store.getters.idToken !== '') {
      if (
        !store.getters.idTokenExpirationDate ||
        store.getters.idTokenExpirationDate < Date.now() / 1000
      ) {
        store.dispatch('tryAutoSignIn');
        while (store.getters.tryAutoSignInFinished === false) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    } else {
      store.dispatch('tryAutoSignIn');
      while (store.getters.tryAutoSignInFinished === false) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    if (store.getters.idToken && store.getters.idToken !== '') {
      headers.Authorization = `Bearer ${store.getters.idToken}`;
    }

    this.instance = () =>
      setup({
        // `axios` options
        baseURL: getEnv('VUE_APP_CMS_API_URL'),
        timeout: 30000,
        headers,
        // `axios-cache-adapter` options
        cache: {
          maxAge: 15 * 60 * 1000,
        },
      });

    this.instanceWithoutHeader = () =>
      setup({
        // `axios` options
        baseURL: getEnv('VUE_APP_CMS_API_URL'),
        timeout: 30000,
        // `axios-cache-adapter` options
        cache: {
          maxAge: 15 * 60 * 1000,
        },
      });

    this.instanceUpload = setup({
      // `axios` options
      baseURL: '',
      timeout: 600000,
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Cache-Control': 'max-age=31536000',
      },
      // `axios-cache-adapter` options
      cache: {
        maxAge: 15 * 60 * 1000,
      },
    });
  }

  // ----------------------------------- ANNEX

  async uploadFile(signedRequest, file) {
    await this.preQuery();
    return this.instanceUpload
      .put(signedRequest, file)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ----------------------------------- AWS Credentials

  async getAwsS3Credentials() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/aws/credentials?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ---------------------------------------------- PAGES

  async getPages(queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/page?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addPage(
    pageName,
    pageContent,
    pageTypeId,
    roleId,
    appTypeId,
    isDraft,
    isActive,
    isDeprecated,
    isStatic,
    isLoggedPage
  ) {
    await this.preQuery();
    const requestBody = {
      page_name: pageName,
      page_content: pageContent,
      page_type_id: pageTypeId,
      role_id: roleId,
      app_type_id: appTypeId,
      is_draft: isDraft,
      is_active: isActive,
      is_deprecated: isDeprecated,
      is_static: isStatic,
      is_logged_page: isLoggedPage,
    };
    return this.instance()
      .post('/page/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updatePage(
    pageId,
    pageName,
    pageContent,
    pageTypeId,
    roleId,
    appTypeId,
    isDraft,
    isActive,
    isDeprecated,
    isStatic,
    isLoggedPage
  ) {
    await this.preQuery();
    const requestBody = {
      page_id: pageId,
      page_name: pageName,
      page_content: pageContent,
      page_type_id: pageTypeId,
      role_id: roleId,
      app_type_id: appTypeId,
      is_draft: isDraft,
      is_active: isActive,
      is_deprecated: isDeprecated,
      is_static: isStatic,
      is_logged_page: isLoggedPage,
    };
    return this.instance()
      .put(`/page/${pageId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async deletePage(pageId) {
    await this.preQuery();
    let route = `/page/${pageId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .delete(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageRendering(url) {
    await this.preQuery();
    const encodedUrl = qs.escape(url);
    return this.instanceWithoutHeader()
      .get(
        `/page/rendering?url=${encodedUrl}&bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageRenderingForPreview(languageCode, pageTypeUrl, pageUrl) {
    await this.preQuery();
    let lcode = languageCode;
    let pturl = pageTypeUrl;
    let purl = pageUrl;
    if (!languageCode) {
      lcode = 'en';
    }
    if (!pageTypeUrl) {
      pturl = '';
    }
    if (!pageUrl) {
      purl = '';
    }
    return this.instanceWithoutHeader()
      .get(
        `/page/rendering/preview?languageCode=${lcode}&pageTypeUrl=${pturl}&pageUrl=${purl}&bearer_token=${
          store.getters.idToken
        }&t=${new Date().toISOString()}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageByPageId(pageId, languageCode) {
    await this.preQuery();
    if (!pageId || pageId === '') {
      return { error: 'no page id provided' };
    }
    return this.instanceWithoutHeader()
      .get(
        `/page/${pageId}/${languageCode || 'en'}?bearer_token=${
          store.getters.idToken
        }`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPagesBySearchQuery(pageName) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/page/search/${pageName}?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageRoutes(queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/page-route?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageRouteByPageId(pageId) {
    await this.preQuery();
    if (!pageId || pageId === '') {
      return { error: 'no page id provided' };
    }
    return this.instanceWithoutHeader()
      .get(`/page/${pageId}/page-route?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addPageRoute(pageId, pageRouteContent, isDeprecated) {
    await this.preQuery();
    const requestBody = {
      page_id: pageId,
      page_route_content: pageRouteContent,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .post('/page-route/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updatePageRoute(pageRouteId, pageId, pageRouteContent, isDeprecated) {
    await this.preQuery();
    const requestBody = {
      page_id: pageId,
      page_route_content: pageRouteContent,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .put(`/page-route/${pageRouteId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ----------------------------------------------------- PAGE TYPES (TEMPLATE)

  async getPageTypes(queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/pageType?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageTypeByPageTypeName(pageTypeName) {
    await this.preQuery();
    const route = `/pageType/name/${pageTypeName}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addPageType(pageTypeName, pageTypeContent) {
    await this.preQuery();
    const requestBody = {
      page_type_name: pageTypeName,
      page_type_content: pageTypeContent,
    };
    return this.instance()
      .post('/pageType/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updatePageType(pageTypeId, pageTypeName, pageTypeContent) {
    await this.preQuery();
    const requestBody = {
      page_type_id: pageTypeId,
      page_type_name: pageTypeName,
      page_type_content: pageTypeContent,
    };
    return this.instance()
      .put(`/pageType/${pageTypeId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------ SECTIONS

  async getReducedSections(queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/section/reduced?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSections(queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/section?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionTypes(queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/sectionType?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionsBySectionTypeId(sectionTypeId) {
    await this.preQuery();
    const route = `/sectionType/${sectionTypeId}/section`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionsBySearchQuery(sectionName) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/section/search/${sectionName}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionsBySearchQueryAndType(sectionName, sectionTypeId) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/section/search/${sectionName}/${sectionTypeId}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionBySectionId(sectionId) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/section/${sectionId}?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionsBySectionIds(sectionIds) {
    await this.preQuery();
    const promises = [];
    sectionIds.forEach((sectionId) => {
      promises.push(this.getSectionBySectionId(sectionId));
    });
    return Promise.all(promises).then((values) => values);
  }

  async updateSectionBoolean(sectionId, fieldToModify, newValue) {
    await this.preQuery();
    const route = `/section/${sectionId}/${fieldToModify}/${newValue}`;
    return this.instance()
      .put(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addSection(
    sectionName,
    sectionContent,
    sectionTypeId,
    roleId,
    appTypeId,
    isDraft,
    isActive,
    isDeprecated
  ) {
    await this.preQuery();
    const requestBody = {
      section_name: sectionName,
      section_content: sectionContent,
      section_type_id: sectionTypeId,
      role_id: roleId,
      app_type_id: appTypeId,
      is_draft: isDraft,
      is_active: isActive,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .post('/section/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateSection(
    sectionId,
    sectionName,
    sectionContent,
    sectionTypeId,
    roleId,
    appTypeId,
    isDraft,
    isActive,
    isDeprecated
  ) {
    await this.preQuery();
    const requestBody = {
      section_id: sectionId,
      section_name: sectionName,
      section_content: sectionContent,
      section_type_id: sectionTypeId,
      role_id: roleId,
      app_type_id: appTypeId,
      is_draft: isDraft,
      is_active: isActive,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .put(`/section/${sectionId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getSectionTypeBySectionTypeName(sectionTypeName) {
    await this.preQuery();
    const route = `/sectionType/name/${sectionTypeName}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // --------------------------------------------- PAGE SECTIONS

  async getPageSectionByPageSectionId(pageSectionId) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/pageSection/${pageSectionId}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPageSectionsLightByPageId(pageId) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/pageSection/light/page/${pageId}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addPageSection(
    pageId,
    sectionId,
    pageSectionOrder,
    sectionDisplay,
    isPublished
  ) {
    await this.preQuery();
    const requestBody = {
      page_id: pageId,
      section_id: sectionId,
      page_section_order: pageSectionOrder,
      section_display: sectionDisplay,
      is_published: isPublished,
    };
    return this.instance()
      .post('/pageSection/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updatePageSection(
    pageSectionId,
    pageId,
    sectionId,
    pageSectionOrder,
    sectionDisplay,
    isPublished
  ) {
    await this.preQuery();
    const requestBody = {
      page_section_id: pageSectionId,
      page_id: pageId,
      section_id: sectionId,
      page_section_order: pageSectionOrder,
      section_display: sectionDisplay,
      is_published: isPublished,
    };
    return this.instance()
      .put(`pageSection/${pageSectionId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updatePageSectionBoolean(pageSectionId, isPublished) {
    await this.preQuery();
    const requestBody = {
      page_section_id: pageSectionId,
      is_published: isPublished,
    };
    return this.instance()
      .put(`pageSection/boolean/${pageSectionId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updatePageSectionOrder(pageSectionId, pageSectionOrder, pageId) {
    await this.preQuery();
    const requestBody = {
      page_section_order: pageSectionOrder,
      page_id: pageId,
    };
    return this.instance()
      .put(`pageSection/order/${pageSectionId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async deletePageSection(pageSectionId) {
    await this.preQuery();
    let route = `/pageSection/${pageSectionId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .delete(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------- Dashboards

  async getDashboards() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/dashboard?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDashboardByDashboardId(dashboardId) {
    await this.preQuery();
    let route = `/dashboard/${dashboardId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addDashboard(pageId, dashboardName, dashboardContent, isDeprecated) {
    await this.preQuery();
    const requestBody = {
      page_id: pageId,
      dashboard_name: dashboardName,
      dashboard_content: dashboardContent,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .post('/dashboard/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateDashboard(
    dashboardId,
    pageId,
    dashboardName,
    dashboardContent,
    isDeprecated
  ) {
    await this.preQuery();
    const requestBody = {
      page_id: pageId,
      dashboard_name: dashboardName,
      dashboard_content: dashboardContent,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .put(`/dashboard/${dashboardId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateDashboardBoolean(dashboardId, fieldToModify, newValue) {
    await this.preQuery();
    const route = `/dashboard/boolean/${dashboardId}/${fieldToModify}/${newValue}`;
    return this.instance()
      .put(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDashboardTemplates() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/dashboard-template?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDashboardTemplateByDashboardTemplateId(dashboardTemplateId) {
    await this.preQuery();
    let route = `/dashboard-template/${dashboardTemplateId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDashboardTemplatesBySearchQuery(dashboardTemplateName) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/dashboard-template/search/${dashboardTemplateName}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------- DATA SERIES

  async getDataSeries() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/data-series?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDataSeriesByDataSeriesId(dataSeriesId) {
    await this.preQuery();
    let route = `/data-series/${dataSeriesId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDataSeriesBySearchQuery(searchQuery) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/data-series/search/${searchQuery}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addDataSeries(dataSeriesName, dataSeriesContent, isDeprecated) {
    await this.preQuery();
    const requestBody = {
      data_series_name: dataSeriesName,
      data_series_content: dataSeriesContent,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .post('/data-series/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateDataSeries(
    dataSeriesId,
    dataSeriesName,
    dataSeriesContent,
    isDeprecated
  ) {
    await this.preQuery();
    const requestBody = {
      data_series_name: dataSeriesName,
      data_series_content: dataSeriesContent,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .put(`/data-series/${dataSeriesId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateDataSeriesBoolean(dataSeriesId, fieldToModify, newValue) {
    await this.preQuery();
    const route = `/data-series/boolean/${dataSeriesId}/${fieldToModify}/${newValue}`;
    return this.instance()
      .put(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------- LANGUAGES

  async getLanguages() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/language?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addLanguage(languageName, languageCode, isDefault) {
    await this.preQuery();
    const requestBody = {
      language_name: languageName,
      language_code: languageCode,
      is_default: isDefault,
    };
    return this.instance()
      .post('/language/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateLanguage(languageId, isDefault) {
    await this.preQuery();
    const requestBody = {
      language_id: languageId,
      is_default: isDefault,
    };
    return this.instance()
      .put(`language/${languageId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------- CSS CLASSES

  async getCSSClasses() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/cssClass?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getCssClassesBySearchQuery(className) {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `/cssClass/search/${className}?bearer_token=${store.getters.idToken}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addCSSClass(cssClassName, cssClassDescription, elementType) {
    await this.preQuery();
    const requestBody = {
      css_class_name: cssClassName,
      css_class_description: cssClassDescription,
      element_type: JSON.stringify(elementType),
    };
    return this.instance()
      .post('/cssClass/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateCSSClass(
    cssClassId,
    cssClassName,
    cssClassDescription,
    elementType
  ) {
    await this.preQuery();
    const requestBody = {
      css_class_name: cssClassName,
      css_class_description: cssClassDescription,
      element_type: JSON.stringify(elementType),
    };
    return this.instance()
      .put(`/cssClass/${cssClassId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async deleteCSSClass(cssClassId) {
    await this.preQuery();
    let route = `/cssClass/${cssClassId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .delete(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------- ARTICLES

  async getArticles() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/article?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getArticleByArticleId(articleId) {
    await this.preQuery();
    let route = `/article/${articleId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getArticleByCategoryId(articleCategoryId) {
    await this.preQuery();
    let route = `/article/category/${articleCategoryId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addArticle(
    articleContent,
    articleCategoryId,
    isDraft,
    isActive,
    isDeprecated
  ) {
    await this.preQuery();
    const requestBody = {
      article_content: articleContent,
      article_category_id: articleCategoryId,
      is_draft: isDraft,
      is_active: isActive,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .post('/article/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateArticle(
    articleId,
    articleContent,
    articleCategoryId,
    isDraft,
    isActive,
    isDeprecated
  ) {
    await this.preQuery();
    const requestBody = {
      article_content: articleContent,
      article_category_id: articleCategoryId,
      is_draft: isDraft,
      is_active: isActive,
      is_deprecated: isDeprecated,
    };
    return this.instance()
      .put(`/article/${articleId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateArticleBoolean(articleId, fieldToModify, newValue) {
    await this.preQuery();
    const route = `/article/boolean/${articleId}/${fieldToModify}/${newValue}`;
    return this.instance()
      .put(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------------ ARTICLE CATEGORIES

  async getArticleCategories() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/articleCategory?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getArticleCategoryByArticleCategoryId(articleCategoryId) {
    await this.preQuery();
    let route = `/articleCategory/${articleCategoryId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async addArticleCategory(articleCategoryContent, articleCategoryTypeId) {
    await this.preQuery();
    const requestBody = {
      article_category_content: articleCategoryContent,
      article_category_type_id: articleCategoryTypeId,
    };
    return this.instance()
      .post('/articleCategory/', qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async updateArticleCategory(
    articleCategoryId,
    articleCategoryContent,
    articleCategoryTypeId
  ) {
    await this.preQuery();
    const requestBody = {
      article_category_content: articleCategoryContent,
      article_category_type_id: articleCategoryTypeId,
    };
    return this.instance()
      .put(`/articleCategory/${articleCategoryId}`, qs.stringify(requestBody))
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async deleteArticleCategory(articleCategoryId) {
    await this.preQuery();
    let route = `/articleCategory/${articleCategoryId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .delete(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // ------------------------------------------------ ARTICLE CATEGORY TYPES

  async getArticleCategoryTypes() {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(`/articleCategoryType?bearer_token=${store.getters.idToken}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getArticleCategoryTypeByArticleCategoryTypeId(articleCategoryTypeId) {
    await this.preQuery();
    let route = `/articleCategoryType/${articleCategoryTypeId}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  // -------------------------------------------- API SECTION

  async getAllGetRoutes() {
    await this.preQuery();
    let route = '/selectRoutes';
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getGetRoute(encodedUrl) {
    await this.preQuery();
    let route = `/selectRoutes/${encodedUrl}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getQueryByRouteUrl(routeUrl, queryParameters = '') {
    await this.preQuery();
    return this.instanceWithoutHeader()
      .get(
        `${routeUrl}?bearer_token=${store.getters.idToken}${
          queryParameters !== '' ? '&' : ''
        }${queryParameters}`
      )
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }
}

export default CmsUtils;
